import { useStore } from "react-redux";
import { useTranslation } from "react-i18next";
import { useEffect, useReducer } from "react";
import { toast } from "react-toastify";
import { AsyncCallArgs, reducerInitialState, searchTableReducer, useAsyncCall } from "@duosoftbg/nacid-components";
import { UseFormReturn } from "react-hook-form";
import useAppDispatch from "../../redux/base/useAppDispatch";
import { GroupName } from "../../../types/filters";
import { updateHasSearchStarted, overrideFiltersData } from "../../../store/redux/slice/SearchData/searchTable";
import { SEARCH_TABLE_CONFIG } from "../../../config/registers/search/filters/groupsConfig";
import { RootState } from "../../../store/redux/store";

type SearchTableControlProps = {
  group: GroupName;
  methods: UseFormReturn<any, any>;
  initialValues: any;
  filterData: (requestFilters: any) => any;
  afterResetFilters?: Function;
  callAfterResetFilters?: boolean;
  initialCall?: boolean;
  syncGroups?: {
    config: { fields: Array<string> };
    groups: Array<GroupName>;
  };
};

const useSearchTableControl = ({
  group,
  methods,
  initialValues,
  filterData,
  afterResetFilters,
  callAfterResetFilters = false,
  initialCall = true,
  syncGroups = null,
}: SearchTableControlProps) => {
  const store = useStore();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { asyncCall } = useAsyncCall();
  const [{ records, total, showList, isLoading, isResetBtnDisabled, isSubmitBtnDisabled, blockTable }, dispatchLocal] =
    useReducer(searchTableReducer, reducerInitialState);

  const handleResetFilters = () => {
    dispatch(overrideFiltersData({ group, data: initialValues }));
    dispatchLocal({ type: "disableResetFiltersButton" });
    methods.reset(initialValues, { keepIsSubmitted: true, keepSubmitCount: true });
    if (afterResetFilters) {
      afterResetFilters();
    }
    dispatchLocal({ type: "afterResetFilters" });
    if (callAfterResetFilters) {
      dispatchLocal({ type: "beforeFetch" });
      fetch();
    }
    toast.info(t("m.resetFilters.success"));
    if (syncGroups) {
      syncGroups.groups.forEach((syncGroup) => {
        dispatch(overrideFiltersData({ group: syncGroup, data: SEARCH_TABLE_CONFIG[syncGroup]?.initValue }));
      });
    }
  };

  const fetch = (showToast = false) => {
    const asyncCallArgs: AsyncCallArgs = {
      promise: filterData(methods.getValues()),
      onSuccess: (response) => {
        dispatchLocal({
          type: "afterFetchSuccess",
          payload: { records: response.content, total: response.totalElements },
        });
        if (showToast) {
          toast.success(t("m.submitFilters.success"));
        }
      },
      onError: () => {
        dispatchLocal({ type: "afterFetchError" });
      },
    };
    asyncCall(asyncCallArgs);
  };

  useEffect(() => {
    const hasStarted = (store.getState() as RootState).SearchData.searchTable[group].hasSearchStarted;
    if (initialCall && hasStarted) {
      dispatchLocal({ type: "beforeFetch" });
      fetch();
    }
    // eslint-disable-next-line
  }, []);

  const handlePageOrOrderChange = () => {
    dispatchLocal({ type: "blockTable" });
    dispatch(overrideFiltersData({ group, data: methods.getValues() }));
    fetch();
  };

  const handleSubmitFilters = (showToast = true) => {
    const resetOrderAndPageData = () => {
      methods.setValue("page", initialValues.page);
      methods.setValue("pageSize", initialValues.pageSize);
      methods.setValue("order", initialValues.order);
      methods.setValue("orderBy", initialValues.orderBy);
    };

    dispatchLocal({ type: "disableSubmitFiltersButton" });
    dispatchLocal({ type: "beforeFetch" });
    resetOrderAndPageData();
    dispatch(updateHasSearchStarted({ group, start: true }));
    dispatch(overrideFiltersData({ group, data: methods.getValues() }));
    fetch(showToast);

    if (syncGroups) {
      let config = syncGroups.config;
      if (config && config.fields) {
        let syncFieldsData = {};

        const originalGroupValues = methods.getValues();
        const fields = config.fields;
        fields.forEach((field) => {
          let value = originalGroupValues[field];
          if (value) {
            syncFieldsData[field] = value;
          }
        });

        syncGroups.groups.forEach((syncGroup) => {
          dispatch(updateHasSearchStarted({ group: syncGroup, start: true }));
          dispatch(
            overrideFiltersData({
              group: syncGroup,
              data: { ...SEARCH_TABLE_CONFIG[syncGroup]?.initValue, ...syncFieldsData },
            }),
          );
        });
      }
    }
  };

  return {
    records,
    total,
    showList,
    isLoading,
    isResetBtnDisabled,
    isSubmitBtnDisabled,
    blockTable,
    handleResetFilters,
    handleSubmitFilters,
    handlePageOrOrderChange,
    fetch,
  };
};

export default useSearchTableControl;
